import { ICardRequestApprovalJourneyStep } from '@Shared/card_request_approval_journey';
import { CARD_REQUEST_ACTIVITY_TYPE } from '@Shared/constants';
import { IFilter } from '@Shared/grid';
import { ICardRequestApprovalRouteModel, ICardRequestModel } from '@Shared/models';
import { CardRequestsSection } from '@Shared/types';

import { Api as apiService } from '../api';

export interface ICardRequestsParams {
  section?: CardRequestsSection;
  limit?: number;
  offset?: number;
  filters?: Record<string, any>;
  include?: string[];
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface ICardRequestsPayload {
  rows: ICardRequestModel[];
  count: number;
}

export type ICreateCardRequestResponse = ICardRequestModel & {
  cardholder_tos_required?: boolean;
  validationErrors?: string[];
};

export type ICreateCardRequestResponse = ICardRequestModel & {
  cardholder_tos_required: boolean;
};

export abstract class CardRequestAPI {
  static readonly cardRequestUrl: string = '/api/card_requests';

  static get(cardRequestId: string): Promise<ICardRequestModel> {
    return apiService.responseToJSONAndToastError(apiService.get(`${CardRequestAPI.cardRequestUrl}/${cardRequestId}`));
  }

  static async getCardRequests(params: ICardRequestsParams): Promise<ICardRequestsPayload> {
    const searchParams = new URLSearchParams({
      section: params.section ? params.section.toString() : '',
      limit: params.limit?.toString(),
      offset: params.offset?.toString(),
      order: params.order || 'desc',
      sort: params.sort || 'created_at'
    });
    const filters: IFilter[] = [];

    if (params.filters?.hasOwnProperty('dismissed')) {
      filters.push({ field: 'dismissed', value: params.filters.dismissed });
    }

    if (params.filters?.hasOwnProperty('user_id')) {
      filters.push({ field: 'user_id', value: params.filters.user_id });
    }

    if (filters.length > 0) {
      searchParams.append('filters', JSON.stringify(filters));
    }

    params.include?.forEach((field) => {
      searchParams.append('include', field);
    });

    return apiService.responseToJSONAndToastError(apiService.get(`${CardRequestAPI.cardRequestUrl}?${searchParams}`));
  }

  static create(data: ICardRequestModel): Promise<ICreateCardRequestResponse> {
    return apiService.responseToJSONAndToastError(apiService.post(CardRequestAPI.cardRequestUrl, data));
  }

  static update(data: ICardRequestModel): Promise<ICardRequestModel> {
    return apiService.responseToJSONAndToastError(apiService.put(CardRequestAPI.cardRequestUrl, data));
  }

  static updateStatus(
    cardRequestId: string,
    status: ICardRequestModel['status'],
    cardRequestAction: CARD_REQUEST_ACTIVITY_TYPE,
    denyReason?: string,
    cardRequestPayload?: Partial<ICardRequestModel>
  ): Promise<ICardRequestModel> {
    return apiService.responseToJSONAndToastError(
      apiService.patch(`${CardRequestAPI.cardRequestUrl}/${cardRequestId}/status`, {
        status,
        denyReason,
        fieldsToUpdate: cardRequestPayload,
        action: cardRequestAction
      })
    );
  }

  static dismiss(cardRequestId: string) {
    return apiService.responseToJSONAndToastError(apiService.patch(`${CardRequestAPI.cardRequestUrl}/${cardRequestId}/dismiss`));
  }

  static remind(cardRequestId: string) {
    return apiService.responseToJSONAndToastError(apiService.post(`${CardRequestAPI.cardRequestUrl}/${cardRequestId}/remind`));
  }

  static denyAllUserCardRequests(userProfileIds: string[]) {
    return apiService.post(`${CardRequestAPI.cardRequestUrl}/deny_all`, { userProfileIds });
  }

  static getJourney(cardRequestId: string): Promise<ICardRequestApprovalJourneyStep[]> {
    return apiService.responseToJSONAndToastError(apiService.get(`${CardRequestAPI.cardRequestUrl}/${cardRequestId}/journey`));
  }

  static getIsIssuableByApprover(cardRequestId: string): Promise<{
    isCardRequestIssuableByApprover: boolean;
    pendingRoutes: ICardRequestApprovalRouteModel[];
  }> {
    return apiService.responseToJSONAndToastError(
      apiService.get(`${CardRequestAPI.cardRequestUrl}/${cardRequestId}/approver_issuable_state`)
    );
  }
}
